import { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import Spinner from "../spinner/Spinner";
import styles from "./css/AddRelation.module.scss";

const AddRelation = () => {
  const { token } = useAuthToken();
  const { id } = useParams();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [relationTitle, setRelationTitle] = useState("");
  const [relationSubTitle, setRelationSubTitle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fileName, setFileName] = useState("No file chosen");
  const [filePreview, setFilePreview] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  // Function to reset form fields
  const resetForm = () => {
    setRelationTitle("");
    setRelationSubTitle("");
    setSelectedStatus("");
    setFileName("No file chosen");
    setFilePreview("");
    setPopupMessage("");
    setErrors({});
  };

  useEffect(() => {
    if (id) {
      const fetchRelationGoalData = async () => {
        try {
          setLoading(true);
          const response = await fetch(
            `${LIVE_URL}/admin/relation-goal/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          const data = await response.json();
          if (response.ok) {
            const relationGoalData = data.data;
            setRelationTitle(relationGoalData.title);
            setRelationSubTitle(relationGoalData.sub_title);
            setSelectedStatus(relationGoalData.status ? "true" : "false");
            setFilePreview(relationGoalData.image);
            setFileName(relationGoalData.image.split("/").pop());
          }
        } catch (error) {
          console.error("Error fetching relation-goal data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchRelationGoalData();
    } else {
      resetForm();
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    let timer;
    if (popupMessage) {
      const popupElement = document.getElementById("popup-message");
      if (popupElement) {
        popupElement.style.opacity = "1";
      }
      timer = setTimeout(() => {
        if (popupElement) {
          popupElement.style.opacity = "0";
        }
        setTimeout(() => {
          setPopupMessage("");
        }, 500);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [popupMessage]);

  const handleTitleChange = (event) => {
    setRelationTitle(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, relationTitle: "" }));
  };

  const handleSubTitleChange = (event) => {
    setRelationSubTitle(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, relationSubTitle: "" }));
  };

  const handleSelectChange = (event) => {
    setSelectedStatus(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, selectedStatus: "" }));
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();
      const fileExtension = fileName.split(".").pop();

      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/svg+xml",
        "image/heic",
      ];
      const validExtensions = ["jpg", "jpeg", "png", "webp", "svg", "heic"];

      if (
        !validTypes.includes(fileType) ||
        !validExtensions.includes(fileExtension)
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          file: "Only valid image files are allowed (JPG,JPEG, PNG, SVG, WEBP,HEIC).",
        }));
        setFileName("No file chosen");
        setFile(null);
        setFilePreview("");
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          file: "File size exceeds 5MB limit.",
        }));
        setFileName("No file chosen");
        setFile(null);
        setFilePreview("");
      } else {
        setFileName(selectedFile.name);
        setFile(selectedFile);
        setFilePreview("");
        setErrors((prevErrors) => ({ ...prevErrors, file: "" }));
      }
    } else {
      setFileName("No file chosen");
      setFile(null);
      setFilePreview("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const newErrors = {};
    if (!relationTitle) newErrors.relationTitle = "❗ Don't Forget the Title!";
    if (!relationSubTitle)
      newErrors.relationSubTitle = "❗ Don't Forget the Subitle!";
    if (!selectedStatus)
      newErrors.selectedStatus = "❗Status field cannot be blank.";
    if (!file && !id) newErrors.file = "❗ Don't forget to add an image!";
    setErrors(newErrors);
    if (errors.file) {
      setPopupMessage("Please select the files.");
      setLoading(false);
      return;
    }
    if (Object.keys(newErrors).length > 0) {
      setPopupMessage("Please fill in all required fields");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("title", relationTitle);
    formData.append("type", "religion_goal");
    formData.append("sub_title", relationSubTitle);
    formData.append("status", selectedStatus === "true");

    if (file) {
      formData.append("image", file);
    }
    if (id) {
      formData.append("id", id);
    }

    try {
      const url = id
        ? `${LIVE_URL}/admin/relation-goal/update`
        : `${LIVE_URL}/admin/relation-goal/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // "Content-Type": "application/json",
        },
        body: formData,
      });

      const responseJson = await response.json();

      if (response.ok) {
        const successMessage = id
          ? "Relation updated successfully."
          : "Relation added successfully.";
        navigate(
          `/home/list-relation?message=${encodeURIComponent(successMessage)}`,
        );
      } else {
        setPopupMessage(`Failed to save relation. Status: ${response.status}`);
      }
    } catch (error) {
      setPopupMessage("Error saving relation.");
    } finally {
      setLoading(false);
    }
  };

  const options = [
    { value: "true", label: "Publish" },
    { value: "false", label: "UnPublish" },
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <div
        id="popup-message"
        className={`${styles.update_message} ${popupMessage.includes(
          "success",
        )}`}
      >
        {popupMessage && <p>{popupMessage}</p>}
      </div>
      <div className={`${styles.add_relation}`}>
        <header>
          <h2>{id ? "Update Relation Goal" : "Add Relation Goal"}</h2>
        </header>
        <section className={`${styles.section}`}>
          <label htmlFor="">Relation Image</label>
          <div className={`${styles.fileInputContainer}`}>
            <input type="file" id="file" onChange={handleFileChange} />
            <label htmlFor="file">Choose File</label>
            <span id="file-name">{fileName}</span>
          </div>

          {errors.file && <p className={styles.error}>{errors.file}</p>}
          <br />
          <label htmlFor="title">Relation Goal Title</label>
          <br />
          <input
            type="text"
            id="relationTitle"
            value={relationTitle}
            onChange={handleTitleChange}
          />
          {errors.relationTitle && (
            <p className={styles.error}>{errors.relationTitle}</p>
          )}
          <br />
          <br />
          <label htmlFor="title">Relation Goal Subtitle</label>
          <br />
          <input
            type="text"
            id="relationSubTitle"
            value={relationSubTitle}
            onChange={handleSubTitleChange}
          />
          {errors.relationSubTitle && (
            <p className={styles.error}>{errors.relationSubTitle}</p>
          )}
          <br />
          <br />
          <label htmlFor="status">Relation Goal Status</label>
          <br />
          <div className={`${styles.select_container}`}>
            <select value={selectedStatus} onChange={handleSelectChange}>
              <option value="">Select Status</option>
              {options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <FaChevronDown className={`${styles.chevron_down}`} />
          </div>
          {errors.selectedStatus && (
            <p className={styles.error}>{errors.selectedStatus}</p>
          )}
          <br />

          <div className={`${styles.button_container}`}>
            <button onClick={() => navigate("/home/list-relation")}>
              Cancel
            </button>
            <button onClick={handleSubmit}>
              {id ? "Update Relation Goal" : "Add Relation Goal"}
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default AddRelation;
